import { Flex, Tag } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  CUSTOMER_HISTORY,
  type CustomerBooking,
  type CustomerFinancing,
  type CustomerLead,
  type CustomerQuotation,
} from './api';
import { HistoryCard } from './HistoryCard';
import { HistorySection } from './HistorySection';

/** Leads, quotations, bookings and financing for one customer, each from its own list. */
export function SalesTab({ customerUid }: { customerUid: string }): ReactNode {
  const { t } = useTranslation();
  const leadStage = (stage: string): string => t(`customers.history.leadStage.${stage}`, { defaultValue: stage });
  const quotationStatus = (s: string): string => t(`customers.history.quotationStatus.${s}`, { defaultValue: s });
  const bookingStatus = (s: string): string => t(`customers.history.bookingStatus.${s}`, { defaultValue: s });
  // On the Till first, then a bike an override took, then the plain status.
  const bookingState = (row: CustomerBooking, status: string): string =>
    row.awaitingDeposit ? 'AWAITING_DEPOSIT' : row.bikeTaken ? 'BIKE_TAKEN' : status;
  const financingStatus = (s: string): string => t(`customers.history.financingStatus.${s}`, { defaultValue: s });

  return (
    <Flex vertical gap={24}>
      <HistorySection<CustomerLead>
        list="leads"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.leads(customerUid, cursor)}
        title={t('customers.history.leads.title')}
        empty={t('customers.history.leads.empty')}
        forbidden={t('customers.history.leads.forbidden')}
        href={(row) => `/leads/${row.uid}`}
        columns={[
          { title: t('customers.history.col.leadNo'), dataIndex: 'leadNo', width: 180 },
          { title: t('customers.history.col.stage'), dataIndex: 'stage', render: (v: string) => <Tag>{leadStage(v)}</Tag> },
          { title: t('customers.history.col.interest'), dataIndex: 'interestModel', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.advisor'), dataIndex: 'assignedAdvisor', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.created'), dataIndex: 'createdAt', width: 130, render: (v: string) => formatDate(v) },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.leadNo}
            status={leadStage(row.stage)}
            lines={[row.interestModel, formatDate(row.createdAt)]}
          />
        )}
      />

      <HistorySection<CustomerQuotation>
        list="quotations"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.quotations(customerUid, cursor)}
        title={t('customers.history.quotations.title')}
        empty={t('customers.history.quotations.empty')}
        forbidden={t('customers.history.quotations.forbidden')}
        href={(row) => `/quotations/${row.uid}`}
        columns={[
          { title: t('customers.history.col.quotationNo'), dataIndex: 'quotationNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{quotationStatus(v)}</Tag>,
          },
          { title: t('customers.history.col.model'), dataIndex: 'model', render: (v: string | null) => orDash(v) },
          {
            title: t('customers.history.col.total'),
            key: 'total',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.grandTotal, row.currency)}</span>,
          },
          {
            title: t('customers.history.col.validUntil'),
            dataIndex: 'validUntil',
            width: 130,
            render: (v: string) => formatDate(v),
          },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.quotationNo}
            status={quotationStatus(row.status)}
            lines={[row.model, formatMoney(row.grandTotal, row.currency)]}
          />
        )}
      />

      <HistorySection<CustomerBooking>
        list="bookings"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.bookings(customerUid, cursor)}
        title={t('customers.history.bookings.title')}
        empty={t('customers.history.bookings.empty')}
        forbidden={t('customers.history.bookings.forbidden')}
        href={(row) => `/bookings/${row.uid}`}
        columns={[
          { title: t('customers.history.col.bookingNo'), dataIndex: 'bookingNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string, row) => <Tag>{bookingStatus(bookingState(row, v))}</Tag>,
          },
          {
            title: t('customers.history.col.bike'),
            key: 'bike',
            render: (_: unknown, row) => orDash(row.model ?? row.quotedBike),
          },
          { title: t('customers.history.col.chassisNo'), dataIndex: 'chassisNo', render: (v: string | null) => orDash(v) },
          {
            title: t('customers.history.col.deposit'),
            key: 'deposit',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.depositAmount, row.currency)}</span>,
          },
          { title: t('customers.history.col.created'), dataIndex: 'createdAt', width: 130, render: (v: string) => formatDate(v) },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.bookingNo}
            status={bookingStatus(bookingState(row, row.status))}
            lines={[row.model ?? row.quotedBike, row.chassisNo]}
          />
        )}
      />

      <HistorySection<CustomerFinancing>
        list="financing"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.financing(customerUid, cursor)}
        title={t('customers.history.financing.title')}
        empty={t('customers.history.financing.empty')}
        forbidden={t('customers.history.financing.forbidden')}
        // Financing is worked on its booking's page; there is no page of its own.
        href={(row) => `/bookings/${row.bookingUid}`}
        columns={[
          { title: t('customers.history.col.applicationNo'), dataIndex: 'applicationNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{financingStatus(v)}</Tag>,
          },
          { title: t('customers.history.col.bank'), dataIndex: 'providerName' },
          {
            title: t('customers.history.col.amount'),
            key: 'amount',
            align: 'right',
            render: (_: unknown, row) => <span className="tabular">{formatMoney(row.amount, row.currency)}</span>,
          },
          {
            title: t('customers.history.col.tenure'),
            dataIndex: 'tenureMonths',
            render: (v: number) => t('customers.history.months', { count: v }),
          },
          { title: t('customers.history.col.bookingNo'), dataIndex: 'bookingNo', width: 180 },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.applicationNo}
            status={financingStatus(row.status)}
            lines={[row.providerName, formatMoney(row.amount, row.currency)]}
          />
        )}
      />
    </Flex>
  );
}
